<script>
  import { DpsLink } from "diplanung-style";
  import { mapStores } from "pinia";

  import AppPageHeaderDropdown from "@/components/AppPageHeaderDropdown.vue";
  import { getSystemParameterBinary } from "@/services/admin.js";
  import { getEnvironmentLinks } from "@/services/location.ts";
  import { useAppStore } from "@/stores/app.js";
  import { useFundamentalStore } from "@/stores/fundamental.js";

  export default {
    name: "ThePageHeader",
    components: {
      DpsLink,
      AppPageHeaderDropdown,
    },
    props: {
      userName: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        logoBinaryUrl: undefined,
      };
    },
    computed: {
      ...mapStores(useAppStore, useFundamentalStore),
      isNutzerResolved() {
        return this.appStore.resolved["/nutzer"];
      },
      isAdminPage() {
        return (this.$route?.fullPath?.includes("/admin") && this.hasAdminRights) || false;
      },
      environmentLinks() {
        return getEnvironmentLinks();
      },
      userInfos() {
        return this.appStore.resolved["/nutzer"] === true
          ? this.fundamentalStore.userSettings
          : undefined;
      },
      userRole() {
        const rollen = this.userInfos?.rollen ?? [];

        return rollen.length === 0
          ? "keine Rollen vorhanden"
          : rollen.reduce((joinedRollen, rolle) => joinedRollen + ", " + rolle);
      },
      verfahrensverantwortung() {
        return this.userInfos?.verfahrensverantwortung?.name;
      },
      zustaendigkeit() {
        return this.fundamentalStore.isUserWithZustaendigkeit
          ? this.userInfos?.zustaendigkeit?.name
          : false;
      },
      hasAdminRights() {
        return this.fundamentalStore.checkUserPermission("ADMIN");
      },
      logoValue() {
        return this.fundamentalStore.getSystemParameterByName("MANDANT_LOGO")?.wert;
      },
      logoMimeType() {
        let fileEnding = this.logoValue ? this.logoValue?.split(".").pop() : "";

        switch (fileEnding.toLowerCase()) {
          case "svg":
            return "image/svg+xml";
          case "png":
            return "image/png";
          case "jpeg":
          case "jpg":
            return "image/jpeg";
          default:
            return undefined;
        }
      },
      hasLogoData() {
        return !!(this.logoMimeType && this.logoBinaryUrl);
      },
    },
    watch: {
      logoValue: {
        handler() {
          this.getLogoBinaryUrl();
        },
      },
      isNutzerResolved: {
        async handler(value) {
          if (value) {
            await this.fundamentalStore.loadSystemParameter("MANDANT_LOGO");
          }
        },
      },
    },
    async beforeMount() {
      if (this.isNutzerResolved) {
        await this.fundamentalStore.loadSystemParameter("MANDANT_LOGO");
      }
    },
    methods: {
      getLogoBinaryUrl() {
        if (this.logoValue) {
          getSystemParameterBinary("MANDANT_LOGO")
            .then((response) => {
              // create new blob to change mime type
              const blob = new Blob([response.data], { type: this.logoMimeType });

              this.logoBinaryUrl = URL.createObjectURL(blob);
            })
            .catch((error) => {
              console.error("Logo konnte nicht geladen werden: ", error);
              this.logoBinaryUrl = undefined;
            });
        }
      },
    },
  };
</script>

<template>
  <header class="dpw-page-header">
    <router-link
      class="dpw-page-header__logo-link"
      title="DiPlanung WeSIA - Startseite"
      :to="isNutzerResolved ? '/' : '/unauthorized'"
    >
      <img
        class="dpw-page-header__logo-link-image"
        :src="hasLogoData ? logoBinaryUrl : '/assets/logo.svg'"
        alt="DiPlanung WeSIA Logo"
      />
      <span class="dpw-page-header__logo-link-label">DiPlanung</span>
    </router-link>

    <div class="dpw-page-header__navigation-wrapper">
      <AppPageHeaderDropdown
        id="pageHeaderNavigationDropdown"
        :button-label="isAdminPage ? 'WeSIA: Fachliche Leitstelle' : 'WeSIA'"
        button-title="Weitere Optionen anzeigen"
        :active="hasAdminRights"
      >
        <li v-if="hasAdminRights && !isAdminPage">
          <router-link class="dropdown-item" to="/admin"> WeSIA: Fachliche Leitstelle </router-link>
        </li>
        <li v-if="isAdminPage">
          <router-link class="dropdown-item" to="/">WeSIA</router-link>
        </li>
      </AppPageHeaderDropdown>
    </div>

    <nav class="dpw-page-header__nav">
      <DpsLink href="/ueber" title="Über WeSIA">Über WeSIA</DpsLink>
    </nav>

    <div class="dpw-page-header__user-wrapper">
      <template v-if="userName && isNutzerResolved">
        <AppPageHeaderDropdown
          id="pageHeaderUserDropdown"
          second-icon="profile"
          button-title="Nutzerinformationen anzeigen"
        >
          <li>
            <DpsLink href="#" :title="userName">{{ userName }}</DpsLink>
          </li>
          <li>
            <p class="dpw-page-header__user-wrapper-role">{{ userRole }}</p>
          </li>
          <li v-if="verfahrensverantwortung">
            <p class="dpw-page-header__user-wrapper-management">
              {{ verfahrensverantwortung }}
            </p>
          </li>
          <li v-if="zustaendigkeit">
            <p class="dpw-page-header__user-wrapper-management">
              {{ zustaendigkeit }}
            </p>
          </li>
          <li><hr class="dropdown-divider" /></li>
          <li>
            <router-link class="dropdown-item" title="Abmelden" to="/logout">
              <span class="dps-icon dps-icon--logout"></span>
              Abmelden
            </router-link>
          </li>
        </AppPageHeaderDropdown>
      </template>
      <template v-else>
        <DpsLink href="/" title="Anmelden">Anmelden</DpsLink>
      </template>
    </div>
  </header>
</template>

<style lang="scss">
  @use "sass:map";
  @use "diplanung-style/scss/variables/typography" as typography-variables;
  @import "@/styles/imports";

  .dpw-page-header {
    @include navigation-spacing;

    display: flex;
    background: var(--dps-color-white);
    border-top: 6px solid var(--dps-color-primary-shade);
    min-height: 86px;
    align-items: center;
    color: var(--dps-color-black);
    font-size: 15px;
    position: sticky;
    top: 0;
    z-index: 3;

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 8px;
      background: linear-gradient(180deg, #f2f2f2 0%, rgb(242 242 242 / 0%) 100%);
    }

    &__logo-link {
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      color: inherit;
      padding-bottom: 2px;
      gap: 12px;
      font-weight: map.get(typography-variables.$font-weight-values, "semi-bold");
      font-size: inherit;
      transition: none;

      &:hover,
      &:focus,
      &:active {
        color: var(--dps-color-primary);
      }
    }

    &__logo-link-image {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 39px;
    }

    &__navigation-wrapper {
      display: flex;
      align-items: center;
      font-weight: map.get(typography-variables.$font-weight-values, "semi-bold");
      margin-right: 24px;

      &::before {
        content: "";
        width: 1px;
        height: 22px;
        background: #949494;
        margin: 0 8px;
        display: inline-block;
      }
    }

    &__nav {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 22px;
      height: 100%;
      overflow-x: auto;
    }

    a {
      color: var(--dps-color-black);

      &:hover,
      &:focus,
      &:active {
        text-decoration: none;
      }
    }

    .dps-link {
      .dps-link__text {
        color: var(--dps-color-black);
        font-size: 15px;
        font-weight: 400;
        white-space: nowrap;
      }

      &:hover,
      &:focus,
      &:active {
        .dps-link__text {
          text-decoration: none;
          color: var(--dps-color-primary);
        }
      }
    }

    &__user-wrapper {
      display: flex;
      align-items: center;

      .dpw-page-header__nav + & {
        margin-left: 24px;
      }

      &-role,
      &-management {
        color: var(--dps-color-dark-gray);
      }
    }
  }
</style>
